/**
 * URL state for the search experience — the URL is the single source of
 * truth for the query, mode, filters, paging, and the opened document.
 *
 * The route's `validateSearch` runs this `parseSearchParams` so every read of
 * `Route.useSearch()` is already validated/normalised. Helpers here convert
 * the flat, URL-friendly search params into the nested {@link SearchFilters}
 * the API expects and back again.
 */
import { DocumentKindEnum, type SearchFilters } from "@records-monitor/shared";
import { z } from "zod";

/** The three search experiences. Mirrors the API's three endpoints. */
export const SearchModeSchema = z.enum(["keyword", "semantic", "ask"]);
export type SearchMode = z.infer<typeof SearchModeSchema>;

/**
 * Coerce a value (which may be a single string from the URL, an array, or
 * undefined) into a string array, dropping empties.
 */
function toStringArray(value: unknown): string[] | undefined {
  if (value === undefined || value === null) return undefined;
  const raw = Array.isArray(value) ? value : [value];
  const cleaned = raw.map(String).filter((s) => s.length > 0);
  return cleaned.length > 0 ? cleaned : undefined;
}

/**
 * Validated search params. All optional so a bare `/search` is valid. Filter
 * fields are flattened (one query param each) for clean, shareable URLs.
 */
export const SearchParamsSchema = z
  .object({
    /** The user's query / question text. */
    q: z.string().optional(),
    /** Active search mode; defaults to keyword. */
    mode: SearchModeSchema.catch("keyword").default("keyword"),
    /** Filter: jurisdiction UUIDs (multi-select). */
    jurisdictionIds: z.preprocess(toStringArray, z.array(z.string()).optional()),
    /** Filter: meeting body names (multi-select / free text). */
    bodyNames: z.preprocess(toStringArray, z.array(z.string()).optional()),
    /** Filter: document kinds. */
    documentKinds: z.preprocess(toStringArray, z.array(DocumentKindEnum).optional()),
    /** Filter: inclusive meeting-date lower bound (YYYY-MM-DD). */
    dateFrom: z.string().optional(),
    /** Filter: inclusive meeting-date upper bound (YYYY-MM-DD). */
    dateTo: z.string().optional(),
    /** Opaque pagination cursor returned by the prior page. */
    cursor: z.string().optional(),
    /** Opened document id (drawer). */
    doc: z.string().optional(),
    /** Opened chunk id (drawer highlight target). */
    chunk: z.string().optional(),
  })
  .strip();

export type SearchParams = z.infer<typeof SearchParamsSchema>;

/** Parse arbitrary `Record<string, unknown>` search params from the router. */
export function parseSearchParams(input: Record<string, unknown>): SearchParams {
  return SearchParamsSchema.parse(input);
}

/** A date-only string (YYYY-MM-DD) widened to an ISO date-time at UTC. */
function toIsoDateTime(date: string, endOfDay: boolean): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
  return `${date}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}Z`;
}

/**
 * Build the API-shaped {@link SearchFilters} (nested, ISO date-times) from the
 * flat URL params. Returns `undefined` when no filter is set so we can omit
 * the `filters` key from request bodies entirely.
 */
export function paramsToFilters(params: SearchParams): SearchFilters | undefined {
  const filters: SearchFilters = {};
  if (params.jurisdictionIds?.length) filters.jurisdictionIds = params.jurisdictionIds;
  if (params.bodyNames?.length) filters.bodyNames = params.bodyNames;
  if (params.documentKinds?.length) filters.documentKinds = params.documentKinds;
  const from = params.dateFrom ? toIsoDateTime(params.dateFrom, false) : undefined;
  const to = params.dateTo ? toIsoDateTime(params.dateTo, true) : undefined;
  if (from) filters.meetingDateFrom = from;
  if (to) filters.meetingDateTo = to;
  return Object.keys(filters).length > 0 ? filters : undefined;
}

/** True if any facet filter is currently set. */
export function hasActiveFilters(params: SearchParams): boolean {
  return Boolean(
    params.jurisdictionIds?.length ||
    params.bodyNames?.length ||
    params.documentKinds?.length ||
    params.dateFrom ||
    params.dateTo,
  );
}

/** The keys that "clear all filters" should reset (leaves q/mode/doc intact). */
export const FILTER_KEYS = [
  "jurisdictionIds",
  "bodyNames",
  "documentKinds",
  "dateFrom",
  "dateTo",
] as const satisfies readonly (keyof SearchParams)[];
